import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  addToast,
  Autocomplete,
  AutocompleteItem,
  Button,
  Chip,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from '@structural-climbing/ui'
import { useRole, useStaffPermissions } from '@structural-climbing/auth'
import { useGymContext } from '../../contexts/GymContext'
import { ChildProfileModal } from '../members/ChildProfileModal'
import { MemberAvatar } from '../members/MemberAvatar'
import { MemberIdentity } from '../members/MemberIdentity'
import { MemberProfileModal } from '../members/MemberProfileModal'
import { PhoneBookingModal } from './PhoneBookingModal'
import { useCalendarApi } from './useCalendarApi'
import type { ClassBooking, ClassSession, EligibleMember, FoundMember, PersonCard } from './types'
import { ageBand, dayLabel, timeOf } from './weekRange'

function fullName(p: Pick<PersonCard, 'first_name' | 'last_name'> | null): string {
  return p ? [p.first_name, p.last_name].filter(Boolean).join(' ') : ''
}

/** "Ana Pop (child of Maria Pop)", or the adult's own name. */
function personLabel(
  profile: PersonCard | null,
  child: { first_name: string; last_name: string | null } | null,
  childOf: (parent: string) => string,
): { name: string; sub: string | null } {
  if (child) {
    return {
      name: [child.first_name, child.last_name].filter(Boolean).join(' '),
      sub: childOf(fullName(profile) || profile?.email || ''),
    }
  }
  return { name: fullName(profile) || profile?.email || '', sub: profile?.email ?? null }
}

interface Props {
  session: ClassSession | null
  canManage: boolean
  onClose: () => void
  onEdit: (session: ClassSession) => void
  /** Something changed that the calendar should re-read. */
  onChanged: () => void
}

export function ClassDrawer({ session, canManage, onClose, onEdit, onChanged }: Props) {
  const { t, i18n } = useTranslation()
  const { primaryColor } = useGymContext()
  const api = useCalendarApi()
  const role = useRole()
  const staffPermissions = useStaffPermissions()
  // Booking by phone adds a person, so it needs Members as well as Calendar.
  const canAddPeople = canManage && (role === 'gym_owner' || staffPermissions?.members === true)

  const [bookings, setBookings] = useState<ClassBooking[]>([])
  const [eligible, setEligible] = useState<EligibleMember[]>([])
  const [loading, setLoading] = useState(false)
  const [adding, setAdding] = useState(false)
  const [confirm, setConfirm] = useState<'cancel' | 'stop' | null>(null)
  const [working, setWorking] = useState(false)
  const [profileTarget, setProfileTarget] = useState<string | null>(null)
  const [childTarget, setChildTarget] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [found, setFound] = useState<FoundMember[]>([])
  const [phoneBooking, setPhoneBooking] = useState(false)

  const load = useCallback(async () => {
    if (!session) return
    const [b, e] = await Promise.all([
      api<{ bookings: ClassBooking[] }>(`/class-sessions/${session.id}/bookings`),
      api<{ members: EligibleMember[] }>(`/class-sessions/${session.id}/eligible`),
    ])
    if (b.ok) setBookings(b.data.bookings)
    if (e.ok) setEligible(e.data.members)
    setLoading(false)
  }, [api, session])

  useEffect(() => {
    setBookings([])
    setEligible([])
    setQuery('')
    setFound([])
    if (!session) return
    setLoading(true)
    void load()
  }, [session, load])

  // A manager may book anyone, plan or not: search the gym as they type.
  const sessionId = session?.id
  useEffect(() => {
    const q = query.trim()
    if (!canManage || !sessionId || q.length < 2) {
      setFound([])
      return
    }
    let stale = false
    const timer = setTimeout(() => {
      void api<{ members: FoundMember[] }>(
        `/class-sessions/${sessionId}/search?q=${encodeURIComponent(q)}`,
      ).then((res) => {
        if (!stale && res.ok) setFound(res.data.members)
      })
    }, 250)
    return () => {
      stale = true
      clearTimeout(timer)
    }
  }, [api, canManage, query, sessionId])

  const withPlan = useMemo(
    () => new Set(eligible.map((m) => `${m.userId}:${m.childId ?? ''}`)),
    [eligible],
  )
  const options = useMemo(
    () => [
      ...eligible.map((m) => ({ ...m, phone: null as string | null, hasPlan: true })),
      ...found
        .filter((m) => !withPlan.has(`${m.userId}:${m.childId ?? ''}`))
        .map((m) => ({ ...m, hasPlan: false })),
    ],
    [eligible, found, withPlan],
  )

  if (!session) return null

  const childOf = (parent: string) => t('calendar.childOf', { parent })
  const band = ageBand(session.min_age, session.max_age)
  const day = dayLabel(session.starts_at.slice(0, 10), i18n.language)
  const full = bookings.length >= session.capacity

  async function add(key: string) {
    const member = options.find((m) => `${m.userId}:${m.childId ?? ''}` === key)
    if (!member) return
    await book(member.userId, member.childId)
  }

  /** Books one person; true when they are in. */
  async function book(userId: string, childId: string | null): Promise<boolean> {
    if (!session) return false
    setAdding(true)
    const res = await api<{ withoutPlan?: boolean; reason?: string }>(
      `/class-sessions/${session.id}/bookings`,
      'POST',
      { userId, childId },
    )
    setAdding(false)
    if (!res.ok) {
      const code = res.data.code
      addToast({
        title:
          code === 'CLASS_FULL'
            ? t('calendar.errorFull')
            : res.data.reason === 'no_entries'
              ? t('calendar.errorNoEntries')
              : code === 'NOT_ELIGIBLE'
                ? t('calendar.errorNotEligible')
                : code === 'PAST'
                  ? t('calendar.errorPast')
                  : t('calendar.errorSave'),
        color: 'danger',
      })
      return false
    }
    if (res.data.withoutPlan) addToast({ title: t('calendar.bookedWithoutPlan') })
    setQuery('')
    await load()
    onChanged()
    return true
  }

  async function remove(bookingId: string) {
    const res = await api(`/class-bookings/${bookingId}`, 'DELETE')
    if (!res.ok) {
      addToast({ title: t('calendar.errorSave'), color: 'danger' })
      return
    }
    await load()
    onChanged()
  }

  async function runConfirm() {
    if (!session || !confirm) return
    setWorking(true)
    const res =
      confirm === 'cancel'
        ? await api<{ removedBookings: number }>(`/class-sessions/${session.id}/cancel`, 'POST')
        : await api<{ removedBookings: number }>(
            `/class-series/${session.series_id}?from=${session.starts_at.slice(0, 10)}`,
            'DELETE',
          )
    setWorking(false)
    setConfirm(null)
    if (!res.ok) {
      addToast({ title: t('calendar.errorSave'), color: 'danger' })
      return
    }
    addToast({
      title:
        confirm === 'cancel'
          ? t('calendar.cancelled', { count: res.data.removedBookings })
          : t('calendar.stopped', { count: res.data.removedBookings }),
    })
    onChanged()
    onClose()
  }

  return (
    <>
      {/* Steps aside while a profile is open: the profile is its own full-screen
          layer, and the drawer, added to the page later, would sit on top of it.
          Closing the profile brings the class back. */}
      <Drawer
        isOpen={session !== null && !profileTarget && !childTarget}
        onClose={onClose}
        size="md"
      >
        <DrawerContent>
          <DrawerHeader className="flex flex-col gap-1">
            <p className="text-xs font-medium uppercase tracking-wide text-foreground/50">
              {day.weekday} {day.date} · {timeOf(session.starts_at)}–{timeOf(session.ends_at)}
            </p>
            <h2 className="text-lg font-semibold">{session.title}</h2>
            <div className="flex flex-wrap gap-1.5">
              <Chip size="sm" variant="flat">
                {band ? t('calendar.ageBand', { band }) : t('calendar.allAges')}
              </Chip>
              <Chip size="sm" variant="flat" color={full ? 'danger' : 'primary'}>
                {t('calendar.placesCount', { booked: bookings.length, capacity: session.capacity })}
              </Chip>
              {session.series_id && (
                <Chip size="sm" variant="flat" color="secondary">
                  {t('calendar.weekly')}
                </Chip>
              )}
            </div>
          </DrawerHeader>
          <DrawerBody className="gap-5">
            {session.trainer && (
              <div className="flex flex-col gap-1.5">
                <p className="text-xs font-semibold uppercase tracking-wide text-foreground/40">
                  {t('calendar.fieldTrainer')}
                </p>
                <MemberIdentity
                  userId={session.trainer.id}
                  name={fullName(session.trainer)}
                  nickname={session.trainer.nickname}
                  avatarExt={session.trainer.avatar_ext}
                  avatarUpdatedAt={session.trainer.avatar_updated_at}
                  primaryColor={primaryColor}
                />
              </div>
            )}
            {session.description && (
              <p className="whitespace-pre-line text-sm text-foreground/70">
                {session.description}
              </p>
            )}

            <div className="flex flex-col gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-foreground/40">
                {t('calendar.roster')}
              </p>
              {loading ? (
                <Spinner size="sm" />
              ) : bookings.length === 0 ? (
                <p className="text-sm text-foreground/50">{t('calendar.rosterEmpty')}</p>
              ) : (
                bookings.map((b) => {
                  const label = personLabel(b.profile, b.child, childOf)
                  return (
                    <div key={b.id} className="flex items-center gap-2">
                      <Button
                        variant="light"
                        className="h-auto min-w-0 flex-1 justify-start px-2 py-1.5"
                        title={t('members.viewProfile')}
                        onPress={() =>
                          b.childId ? setChildTarget(b.childId) : setProfileTarget(b.userId)
                        }
                      >
                        <MemberIdentity
                          userId={b.childId ? null : b.userId}
                          name={label.name}
                          nickname={b.childId ? null : b.profile?.nickname}
                          email={label.sub}
                          avatarExt={b.childId ? null : b.profile?.avatar_ext}
                          avatarUpdatedAt={b.childId ? null : b.profile?.avatar_updated_at}
                          primaryColor={primaryColor}
                        />
                      </Button>
                      {b.withoutPlan && (
                        <Chip size="sm" variant="flat" color="warning" className="shrink-0">
                          {t('calendar.noPlanChip')}
                        </Chip>
                      )}
                      <Button
                        size="sm"
                        variant="light"
                        color="danger"
                        onPress={() => void remove(b.id)}
                      >
                        {t('calendar.removeFromClass')}
                      </Button>
                    </div>
                  )
                })
              )}
            </div>

            {!full && (
              <div className="flex flex-col gap-2">
                <Autocomplete
                  label={t('calendar.addMember')}
                  description={
                    canManage ? t('calendar.addMemberHintManager') : t('calendar.addMemberHint')
                  }
                  isDisabled={adding || loading}
                  inputValue={query}
                  onInputChange={setQuery}
                  selectedKey={null}
                  onSelectionChange={(key) => {
                    if (key) void add(String(key))
                  }}
                  listboxProps={{
                    emptyContent: canManage
                      ? t('calendar.noMatchManager')
                      : t('calendar.noEligible'),
                  }}
                >
                  {options.map((m) => {
                    const label = personLabel(m.profile, m.child, childOf)
                    const sub = [label.sub, m.phone].filter(Boolean).join(' · ') || null
                    // The phone and nickname too, so what the search matched on
                    // is not filtered out again here.
                    const text = [label.name, sub, m.profile.nickname].filter(Boolean).join(' · ')
                    return (
                      // title and description, not custom children: HeroUI labels the
                      // option from its title slot, so children would leave it nameless.
                      <AutocompleteItem
                        key={`${m.userId}:${m.childId ?? ''}`}
                        textValue={text}
                        title={label.name}
                        description={sub}
                        startContent={
                          <MemberAvatar
                            userId={m.childId ? null : m.userId}
                            name={label.name}
                            avatarExt={m.childId ? null : m.profile.avatar_ext}
                            avatarUpdatedAt={m.childId ? null : m.profile.avatar_updated_at}
                            primaryColor={primaryColor}
                          />
                        }
                        endContent={
                          m.hasPlan ? null : (
                            <Chip size="sm" variant="flat" color="warning">
                              {t('calendar.noPlanChip')}
                            </Chip>
                          )
                        }
                      />
                    )
                  })}
                </Autocomplete>
                {canAddPeople && (
                  <div>
                    <Button
                      size="sm"
                      variant="flat"
                      isDisabled={adding || loading}
                      onPress={() => setPhoneBooking(true)}
                    >
                      {t('calendar.phoneBookingOpen')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DrawerBody>
          {canManage && (
            <DrawerFooter className="flex flex-wrap justify-between gap-2">
              <div className="flex gap-2">
                <Button variant="flat" color="danger" onPress={() => setConfirm('cancel')}>
                  {t('calendar.cancelClass')}
                </Button>
                {session.series_id && (
                  <Button variant="light" color="danger" onPress={() => setConfirm('stop')}>
                    {t('calendar.stopSeries')}
                  </Button>
                )}
              </div>
              <Button color="primary" onPress={() => onEdit(session)}>
                {t('common.edit')}
              </Button>
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>

      <Modal scrollBehavior="inside" isOpen={confirm !== null} onClose={() => setConfirm(null)}>
        <ModalContent>
          <ModalHeader>
            {confirm === 'stop' ? t('calendar.stopSeriesTitle') : t('calendar.cancelClassTitle')}
          </ModalHeader>
          <ModalBody>
            <p className="text-sm">
              {confirm === 'stop'
                ? t('calendar.stopSeriesBody')
                : t('calendar.cancelClassBody', { count: bookings.length })}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setConfirm(null)}>
              {t('common.cancel')}
            </Button>
            <Button color="danger" isLoading={working} onPress={() => void runConfirm()}>
              {confirm === 'stop' ? t('calendar.stopSeries') : t('calendar.cancelClass')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {phoneBooking && (
        <PhoneBookingModal
          session={session}
          onClose={() => setPhoneBooking(false)}
          onBooked={() => {
            setPhoneBooking(false)
            void load()
            onChanged()
          }}
          onBookExisting={(userId, childId) => book(userId, childId)}
        />
      )}
      {profileTarget && (
        // A trainer reads the profile of someone in their class; acting on them
        // stays with the people assigned to them.
        <MemberProfileModal
          userId={profileTarget}
          readOnly={!canManage}
          onClose={() => setProfileTarget(null)}
        />
      )}
      {childTarget && (
        <ChildProfileModal
          childId={childTarget}
          readOnly={!canManage}
          onClose={() => setChildTarget(null)}
          onOpenParent={(userId) => {
            setChildTarget(null)
            setProfileTarget(userId)
          }}
        />
      )}
    </>
  )
}
